<template>
  <v-dialog :value="value" height="660px" width="990px" persistent @input="$emit('input', false)">
    <v-card class="dialog-content">
      <mf-loading loader-type="dynamic" :dynamic-amount="6" :boolean-loader="tableLoading">
        <v-card-title>
          <p class="block-title">
            {{ campaign.name }}
          </p>
        </v-card-title>
        <v-card-subtitle>
          <span class="description-label">{{ campaign.description }}</span>
          <v-row>
            <v-col cols="4">
              <div class="mb-1 attr-label">Período de validade:</div>
              <v-chip exact-active-class="chip-oferta">
                {{ getPeriod(campaign.start_date.toString(), campaign.end_date.toString()) }}
              </v-chip>
            </v-col>
            <v-col cols="8">
              <div class="mb-1 attr-label">Ativo nas lojas:</div>
              <v-chip v-for="unit in campaign.units" :key="unit._id" class="chip-oferta mr-2 mb-2">
                {{ unit.name }}
              </v-chip>
            </v-col>
          </v-row>
        </v-card-subtitle>
        <v-card-text v-if="!showRadioButtons">
          <v-row justify="center" align="center">
            <v-col cols="12">
              <mf-tabs-menu v-model="offersTypeTabHighLevel" highlightColor="white" :tabs="offersTypeTabsHighLevel">
                <template v-slot:children>
                  <mf-tabs-menu v-model="offersTypeTabLowLevel" :tabs="offersTypeTabsLowLevel" />
                </template>
              </mf-tabs-menu>
            </v-col>
            <v-col cols="12" class="px-9">
              <div v-for="(offer, index) in offersTypeTabsLowLevel" :key="offer.offerSalesKeyName">
                <offer-sale-table-tab
                  v-if="offerType === 'general'"
                  v-show="offersTypeTabLowLevel === index"
                  offer-type="offer_sales"
                  :has-segments="campaign.segments && campaign.segments.length > 0"
                  :all-products="campaign.offer_sales"
                />
                <offer-sale-table-tab
                  v-if="offerType === 'percentage'"
                  v-show="offersTypeTabLowLevel === index"
                  offer-type="percentage_offer_sales"
                  :all-products="campaign.percentage_offer_sales"
                />
                <offer-sale-table-tab
                  v-if="offerType === 'takepay'"
                  v-show="offersTypeTabLowLevel === index"
                  offer-type="takepay_offer_sales"
                  :all-products="campaign.takepay_offer_sales"
                />
                <combo-offer-sales v-if="offerType === 'combo'" v-show="offersTypeTabLowLevel === index" :combos="campaign.combo_offer_sales" />
              </div>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-else class="select-format">
          <h4>Selecionar formato</h4>
          <p>
            Sua oferta possui mais de um formato. Para conseguirmos entregar essas ofertas para seus consumidores de forma mais efetiva, precisamos que você
            selecione apenas um deles.
          </p>

          <v-radio-group v-model="radioOfferType" :disabled="disabled" class="radio-container">
            <v-radio v-for="(offer, index) in offersTypeTabsLowLevel" :key="index" :value="offer.value">
              <template v-slot:label>
                <div>
                  {{ offer.name }}
                  <p>{{ getProductsLabel(offer.itemsLength) }}</p>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>
      </mf-loading>

      <mf-action-buttons
        class="pa-3"
        :primary-button="{
          text: 'Selecionar campanha',
          action: confirm,
          isVisible: true,
          isDisabled: disabled,
        }"
        :extra-button="{
          text: 'Fechar',
          action: cancel,
          isVisible: true,
        }"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { DateTime } from 'luxon'

import { assertArray, getPeriod } from '@/config/helpers.js'
import { QUERY_SALES_CAMPAIGN_BY_ID } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import { getAllSingleProductsOutsideAndInsideGroup } from '@/modules/campanhas/gestao-ofertas-v2/helpers/products.js'
import ComboOfferSales from './DialogTablesTabs/ComboOfferSales.vue'
import OfferSaleTableTab from './DialogTablesTabs/OfferSaleTableTab.vue'

export default {
  name: 'CampaignDetailsDialog',

  components: {
    ComboOfferSales,
    OfferSaleTableTab,
  },

  props: {
    campaignId: {
      type: String,
      required: true,
    },
    selectedType: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: Boolean,
  },

  data: () => ({
    DateTime: DateTime,
    tableLoading: false,
    campaign: {
      name: '',
      description: '',
      eans: [],
      units: [],
      departments: [],
      start_date: '',
      end_date: '',
      offer_sales: [],
      percentage_offer_sales: [],
      takepay_offer_sales: [],
      combo_offer_sales: [],
      active: false,
      segments: [],
      activatable: false,
    },
    offersTypeTabHighLevel: 0,
    offersTypeTabLowLevel: 0,
    offerSalesKeyNames: {
      offer_sales: 'offer_sales',
      percentage_offer_sales: 'percentage_offer_sales',
      takepay_offer_sales: 'takepay_offer_sales',
      combo_offer_sales: 'combo_offer_sales',
      target_products: 'target_products',
      cashback_percentage_offer_sales: 'cashback_percentage_offer_sales',
      cashback_absolute_offer_sales: 'cashback_absolute_offer_sales',
      points_offer_sales: 'points_offer_sales',
    },
    radioOfferType: '',
    offerType: '',
    showRadioButtons: false,
  }),

  computed: {
    isAdvancedOffer() {
      return this.$getAllowedModules()['advanced_discounts']
    },
    isCashbackAbsolute() {
      return this.$getAllowedModules()['cashback']
    },
    isPoints() {
      return this.$getAllowedModules()['points']
    },
    showDiscountTab() {
      return (
        this.allGeneralOfferSalesProducts?.length ||
        this.allPercentageOfferSalesProducts?.length ||
        this.allTakeAndPayOfferSalesProducts?.length ||
        this.allComboOfferSalesProducts?.length
      )
    },
    showCashbackAndPointsTab() {
      if (!this.isCashbackAbsolute && !this.isPoints) return false
      return this.allCashbackPercentageProducts?.length || this.allCashbackAbsoluteProducts?.length || this.allPointsProducts?.length
    },
    offersTypeTabsHighLevel() {
      let offersTabs = []
      const discountAndOffers = {
        name: 'Descontos e ofertas',
        haveLength: false,
        icon: 'sell',
      }
      const cashbackAndPoints = {
        name: 'Cashback e pontos',
        haveLength: false,
        icon: 'currency_exchange',
        disabled: true,
      }

      if (this.showDiscountTab) offersTabs = offersTabs.concat(discountAndOffers)
      if (this.showCashbackAndPointsTab) offersTabs = offersTabs.concat(cashbackAndPoints)

      return offersTabs
    },
    offersTypeTabsLowLevel() {
      return this.allOffersTypeTabs.filter((item) => item.highLevel === this.offersTypeTabHighLevel)
    },
    allOffersTypeTabs() {
      let offersTabs = []

      if (this.allGeneralOfferSalesProducts?.length) {
        offersTabs.push({
          name: 'Desconto absoluto',
          itemsLength: this.allGeneralOfferSalesProducts?.length || 0,
          haveLength: true,
          offerSalesKeyName: this.offerSalesKeyNames.offer_sales,
          value: 'general',
          disabled: this.offerType !== 'general',
          highLevel: 0,
        })
      }

      if (this.isAdvancedOffer) {
        const advancedOffers = [
          ...(this.allPercentageOfferSalesProducts?.length || 0
            ? [
                {
                  name: 'Desconto percentual',
                  itemsLength: this.allPercentageOfferSalesProducts?.length || 0,
                  haveLength: true,
                  offerSalesKeyName: this.offerSalesKeyNames.percentage_offer_sales,
                  value: 'percentage',
                  disabled: this.offerType !== 'percentage',
                  highLevel: 0,
                },
              ]
            : []),
          ...(this.allTakeAndPayOfferSalesProducts?.length || 0
            ? [
                {
                  name: 'Leve e pague',
                  itemsLength: this.allTakeAndPayOfferSalesProducts?.length || 0,
                  haveLength: true,
                  offerSalesKeyName: this.offerSalesKeyNames.takepay_offer_sales,
                  value: 'takepay',
                  disabled: this.offerType !== 'takepay',
                  highLevel: 0,
                },
              ]
            : []),
          ...(this.allComboOfferSalesProducts?.length || 0
            ? [
                {
                  name: 'Combo',
                  itemsLength: this.allComboOfferSalesProducts?.length || 0,
                  haveLength: true,
                  offerSalesKeyName: this.offerSalesKeyNames.combo_offer_sales,
                  value: 'combo',
                  disabled: this.offerType !== 'combo',
                  highLevel: 0,
                },
              ]
            : []),
          ...(this.allCashbackPercentageProducts?.length || 0
            ? [
                {
                  name: 'Cashback percentual',
                  itemsLength: this.allCashbackPercentageProducts?.length || 0,
                  haveLength: true,
                  offerSalesKeyName: this.offerSalesKeyNames.cashback_percentage_offer_sales,
                  value: 'cashback_percentage',
                  disabled: this.offerType !== 'cashback_percentage',
                  highLevel: 1,
                },
              ]
            : []),
          ...(this.allCashbackAbsoluteProducts?.length || 0
            ? [
                {
                  name: 'Cashback absoluto',
                  itemsLength: this.allCashbackAbsoluteProducts?.length || 0,
                  haveLength: true,
                  offerSalesKeyName: this.offerSalesKeyNames.cashback_absolute_offer_sales,
                  value: 'cashback_absolute',
                  /*  disabled: this.offerType !== 'cashback_absolute', */
                  disabled: true,
                  highLevel: 1,
                },
              ]
            : []),
          ...(this.allPointsProducts?.length || 0
            ? [
                {
                  name: 'Pontos',
                  itemsLength: this.allPointsProducts?.length || 0,
                  haveLength: true,
                  offerSalesKeyName: this.offerSalesKeyNames.points_offer_sales,
                  value: 'points',
                  /*  disabled: this.offerType !== 'points', */
                  disabled: true,
                  highLevel: 1,
                },
              ]
            : []),
        ]
        if (!(getAllSingleProductsOutsideAndInsideGroup(this.campaign.offer_sales)?.length || 0)) return advancedOffers
        offersTabs = offersTabs.concat(advancedOffers)
      }
      return offersTabs
    },
    allGeneralOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campaign?.offer_sales)
    },
    allPercentageOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campaign?.percentage_offer_sales)
    },
    allTakeAndPayOfferSalesProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campaign?.takepay_offer_sales)
    },
    allCashbackPercentageProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campaign?.cashback_percentage_offer_sales)
    },
    allCashbackAbsoluteProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campaign?.cashback_absolute_offer_sales)
    },
    allPointsProducts() {
      return getAllSingleProductsOutsideAndInsideGroup(this.campaign?.points_offer_sales)
    },
    allComboOfferSalesProducts() {
      if (this.campaign.combo_offer_sales) {
        const productsArray = this.campaign.combo_offer_sales.reduce((accumulator, current) => {
          accumulator.push(...current.trigger_products)
          accumulator.push(...current.target_products)
          return accumulator
        }, [])

        return getAllSingleProductsOutsideAndInsideGroup(productsArray)
      }

      return []
    },
  },

  watch: {
    async campaignId() {
      await this.getData()
    },
    value(isCampaignDetailsDialogOpen) {
      if (!isCampaignDetailsDialogOpen) {
        this.offersTypeTabHighLevel = 0
        this.offersTypeTabLowLevel = 0
      }
    },
  },

  async mounted() {
    await this.getData()
    if (this.offersTypeTabsLowLevel.length === 1) {
      this.radioOfferType = this.offersTypeTabsLowLevel[0].value
      this.offerType = this.radioOfferType
      return
    }
    if (this.selectedType) this.radioOfferType = this.selectedType
    this.showRadioButtons = true
  },

  methods: {
    getPeriod,
    confirm() {
      if (!this.showRadioButtons) {
        this.$emit('select-oferta', { campaign: this.campaign, offerType: this.radioOfferType })
        this.$emit('input', false)
      } else {
        const tabIndex = this.allOffersTypeTabs.findIndex((tab) => tab.value === this.radioOfferType)
        this.offersTypeTabLowLevel = tabIndex
        this.offerType = this.radioOfferType
        this.showRadioButtons = false
      }
    },
    cancel() {
      this.$emit('reset-modal-counter')
      this.$emit('input', false)
    },
    getProductsLabel(length) {
      return length === 1 ? '1 produto' : `${length} produtos`
    },
    async getData() {
      const formatOfferSalesInGroupsAndSingleProducts = (offerSales, offerSaleType) => {
        const offerSalesFormatted = offerSales.reduce((offerSalesFormatted, offer) => {
          if (offer?.[offerSaleType]) {
            const { [offerSaleType]: groupProducts, ...group } = offer
            const newGroup = {
              origin: 'search',
              original_eans: offer.eans,
              imported: false,
              isGroup: true,
              products: groupProducts.map((product) => ({ active: product.active || true, ...product })),
            }
            offerSalesFormatted.push(Object.assign(newGroup, group))
          } else {
            offerSalesFormatted.push(offer)
          }
          return offerSalesFormatted
        }, [])
        return offerSalesFormatted
      }

      if (this.campaignId) {
        try {
          this.tableLoading = true
          await this.$apollo
            .query({
              query: QUERY_SALES_CAMPAIGN_BY_ID,
              variables: {
                id: this.campaignId,
              },
              context: {
                uri: this.$microservicesUrls['sales'],
              },
              fetchPolicy: 'no-cache',
            })
            .then((response) => {
              const { offerSchedule } = response.data
              this.campaign = {
                ...offerSchedule,
                offer_sales: assertArray(offerSchedule?.offer_sales).length
                  ? formatOfferSalesInGroupsAndSingleProducts(offerSchedule.offer_sales, 'offer_sales')
                  : [],
                percentage_offer_sales: assertArray(offerSchedule?.percentage_offer_sales).length
                  ? formatOfferSalesInGroupsAndSingleProducts(offerSchedule.percentage_offer_sales, 'percentage_offer_sales')
                  : [],
                takepay_offer_sales: assertArray(offerSchedule?.takepay_offer_sales).length
                  ? formatOfferSalesInGroupsAndSingleProducts(offerSchedule.takepay_offer_sales, 'takepay_offer_sales')
                  : [],
                combo_offer_sales: assertArray(offerSchedule?.combo_offer_sales).length
                  ? offerSchedule.combo_offer_sales.map((item) => {
                      item.trigger_products = formatOfferSalesInGroupsAndSingleProducts(item.trigger_products, 'trigger_products')
                      item.target_products = formatOfferSalesInGroupsAndSingleProducts(item.target_products, 'target_products')
                      return item
                    })
                  : [],
              }
              this.tableLoading = false
            })
        } catch (error) {
          this.$snackbar({ message: 'Erro ao buscar dados no servidor', snackbarColor: 'error' })
        } finally {
          this.tableLoading = false
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-content {
  .block-title {
    opacity: 0.8;
    font-size: 22px;
    text-transform: uppercase;
    font-weight: bold;
    color: $cinza-d10;
  }
}

.select-format {
  & h4 {
    color: $cinza-d10;
    font-weight: bold;
  }

  & p {
    color: $cinza-d10;
    font-size: 14px;
    line-height: 150%;
  }

  & .radio-container {
    & ::v-deep .v-input--radio-group__input {
      display: grid !important;
      grid-template-columns: 1fr 1fr;

      & div {
        color: $cinza-d10;
      }

      & p {
        color: $cinza-0;
      }
    }
  }
}

.v-radio {
  align-items: flex-start;
}

.description-label {
  font-size: 18px;
  color: $cinza-d10;
}

.attr-label {
  color: $cinza-d10;
  letter-spacing: 0.4px;
}

div.v-card__subtitle > div > div.col > span {
  background-color: $cinza-l90 !important;
  color: $cinza-0;
}

.size-table {
  height: 100% !important;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 275px;

  /* width */
  &::-webkit-scrollbar {
    width: 10px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: $cinza-l90;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: $cinza-l20;
    border-radius: 10px;
  }
}
</style>
